/**
 * useVoicePlayer — la lecture d'un vocal, un seul à la fois : ceux du fil de la messagerie, la
 * réécoute d'un enregistrement, les vocaux du bilan du tuteur. Un vocal se désigne par un id et son
 * adresse ; `useConversationVoicePlayer` l'habille pour les messages du fil.
 *
 * Le lecteur du design system est contrôlé : il reçoit `playing` et `progress`, et remonte son
 * toucher. Ce crochet est le moteur, celui de la lecture seule de
 * `student/activity/execute/AudioRecordingContainer.js` (`chatMode`, `readOnly`), que les ports
 * n'importent pas :
 *  - `expo-audio`, sans jamais rendre la session audio partagée (`keepAudioSessionActive`) ;
 *  - sur mobile, le fichier est d'abord copié dans le cache (`voiceCache`) : le lecteur natif ne lit
 *    pas la route de la messagerie en flux ; le web la lit directement.
 * Toucher un autre vocal arrête le premier et lance celui-là ; toucher le même le met en pause ou
 * le reprend ; arrivé au bout, il repart du début. Changer de portée (`scopeKey`, le fil) arrête
 * tout : au large, le volet de « Messages » garde la conversation montée d'un tuteur à l'autre.
 *
 * La durée ne se connaît qu'une fois le fichier chargé : seul le vocal en cours la montre. Un vocal
 * local (celui qu'on vient d'enregistrer, ou qui part encore) se lit sans passer par le cache.
 *
 * Chargé, le vocal en cours se règle comme dans l'ancien lecteur (`usePlaybackController`) : on s'y
 * place (`seek`, au lâcher du curseur) et sa vitesse tourne de 1x à 1,5x, 2x, puis 1x (`cycleRate`,
 * la hauteur de la voix corrigée). Un autre vocal repart à 1x : chaque bulle avait son lecteur.
 */
import { useAudioPlayer, useAudioPlayerStatus } from "expo-audio"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Platform } from "react-native"

import { formatVoiceDuration } from "../formatters/voiceFormatter"
import { formatVoiceElapsed, nextVoiceRate } from "../screens/student/Messaging/conversationVoice"
import { cachedVoiceUri } from "../screens/student/Messaging/voiceCache"
import { type UIVoicePlayback } from "../types/studentConversation"

type Active = { messageId: string; uri: string }

export function useVoicePlayer(scopeKey: string | null) {
    const [active, setActive] = useState<Active | null>(null)
    const [playableUri, setPlayableUri] = useState<string | null>(null)
    /** Le geste attend que le fichier soit chargé pour lancer la lecture. */
    const wantsPlayRef = useRef(false)
    const [rate, setRate] = useState(1)

    useEffect(() => {
        setPlayableUri(null)
        if (active == null) return
        // Le web lit l'adresse en flux ; un fichier local se lit tel quel.
        if (Platform.OS === "web" || !/^https?:/i.test(active.uri)) {
            setPlayableUri(active.uri)
            return
        }
        let cancelled = false
        cachedVoiceUri(active.uri)
            .then((uri) => {
                if (!cancelled) setPlayableUri(uri)
            })
            .catch(() => {
                // Le bouton reste sur « Écouter » : un nouveau toucher retente la copie.
                if (!cancelled) setActive(null)
            })
        return () => {
            cancelled = true
        }
    }, [active])

    const player = useAudioPlayer(playableUri != null ? { uri: playableUri } : null, {
        updateInterval: 250,
        keepAudioSessionActive: true,
    })
    const status = useAudioPlayerStatus(player)
    // `useAudioPlayerStatus` ne connaît l'état que par les événements du lecteur, écoutés à partir
    // de son effet. Un fichier déjà en cache se charge plus vite : son « prêt » part avant l'écoute et
    // se perd, et le statut restait « idle » sur un lecteur pourtant prêt — le vocal ne partait
    // jamais. Le lecteur est donc relu une fois l'écoute posée (cet effet vient après celui du statut).
    const [readyPlayer, setReadyPlayer] = useState<typeof player | null>(null)
    useEffect(() => {
        if (playableUri != null && player.isLoaded) setReadyPlayer(player)
    }, [player, playableUri])
    const loaded = playableUri != null && (!!status?.isLoaded || readyPlayer === player)
    const duration = loaded ? status.duration || (player.duration ?? 0) : 0
    const position = loaded ? (status.currentTime ?? 0) : 0

    useEffect(() => {
        if (!loaded || !wantsPlayRef.current) return
        wantsPlayRef.current = false
        player.play()
    }, [loaded, player])

    // Un autre vocal repart à 1x.
    useEffect(() => setRate(1), [active?.messageId])

    // La vitesse choisie s'applique au lecteur chargé — et au suivant, s'il est remplacé.
    useEffect(() => {
        if (!loaded) return
        try {
            player.setPlaybackRate(rate, "medium")
            player.shouldCorrectPitch = true
        } catch {
            // Déjà libéré.
        }
    }, [loaded, player, rate])

    // Une autre portée (un autre fil), ou plus de portée : rien ne continue de jouer sans contrôle visible.
    const scopeRef = useRef(scopeKey)
    useEffect(() => {
        if (scopeRef.current === scopeKey) return
        scopeRef.current = scopeKey
        wantsPlayRef.current = false
        setActive(null)
        try {
            player.pause()
        } catch {
            // Déjà libéré.
        }
    }, [scopeKey, player])

    // Quitter l'écran (ou changer de vocal) arrête ce qui joue.
    useEffect(
        () => () => {
            try {
                player.pause()
            } catch {
                // Déjà libéré.
            }
        },
        [player]
    )

    /** Lance le vocal `id` (à l'adresse `uri`), ou le met en pause, ou le reprend. */
    const toggle = useCallback(
        (id: string, uri: string) => {
            if (active?.messageId !== id) {
                wantsPlayRef.current = true
                setActive({ messageId: id, uri })
                return
            }
            if (!loaded) {
                wantsPlayRef.current = !wantsPlayRef.current
                return
            }
            if (status.playing) {
                player.pause()
                return
            }
            if (duration > 0 && position >= duration - 0.05) void player.seekTo(0)
            player.play()
        },
        [active, loaded, status.playing, player, duration, position]
    )

    /** Arrête ce qui joue sans rien lancer : l'enregistreur s'ouvre, ou recommence. */
    const stop = useCallback(() => {
        wantsPlayRef.current = false
        setActive(null)
        try {
            player.pause()
        } catch {
            // Déjà libéré.
        }
    }, [player])

    /** Se placer dans le vocal en cours, de 0 à 1 — sans effet tant qu'il n'est pas chargé. */
    const seek = useCallback(
        (id: string, ratio: number) => {
            if (active?.messageId !== id || !loaded || !(duration > 0)) return
            void player.seekTo(Math.max(0, Math.min(1, ratio)) * duration)
        },
        [active, loaded, duration, player]
    )

    const cycleRate = useCallback(
        (id: string) => {
            if (active?.messageId !== id || !loaded) return
            setRate(nextVoiceRate)
        },
        [active, loaded]
    )

    const messageId = active?.messageId ?? null
    const playing = loaded && !!status.playing
    const progress = duration > 0 ? Math.min(1, position / duration) : 0
    const seekable = loaded && duration > 0
    const elapsedLabel = seekable ? formatVoiceElapsed(position) : ""
    // L'état du vocal en cours, pour le fil : il bouge pendant l'écoute, posé à part il ne refait pas
    // le fil.
    const playback = useMemo<UIVoicePlayback | null>(
        () => (messageId != null ? { messageId, playing, progress, seekable, rate, elapsedLabel } : null),
        [messageId, playing, progress, seekable, rate, elapsedLabel]
    )

    return {
        messageId,
        playing,
        progress,
        label: formatVoiceDuration(duration),
        /** Chargé : le vocal se règle (curseur, vitesse). */
        seekable,
        rate,
        /** Où en est la lecture, « 0:12 » — « 0:00 » au départ, comme l'ancien lecteur. */
        elapsedLabel,
        /** Ce que le fil montre du vocal en cours ; `null` quand aucun ne joue. */
        playback,
        toggle,
        stop,
        seek,
        cycleRate,
    }
}
